import { useEffect, useRef, useState } from "react";
import { Search, X, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export const IcdLabel = ({ x, testid }) => (
  <span data-testid={testid} className="inline-flex items-baseline gap-1.5">
    <b className="font-mono text-[12px] text-slate-900">{x.code}</b>
    <span className={x.title ? "text-slate-700" : "text-amber-700"}>{x.title || "not in WHO ICD-10 library"}</span>
  </span>
);

function useSearch(q) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setRows([]); return undefined; }
    setBusy(true);
    const t = setTimeout(() => api.get("/icd10/who/search", { params: { q } }).then((r) => setRows(r.data)).finally(() => setBusy(false)), 250);
    return () => clearTimeout(t);
  }, [q]);
  return [rows, busy];
}

// Pick codes from the WHO ICD-10 library. value = [{code, title}]
export function IcdPicker({ value = [], onChange, multiple = true, testid = "icd-picker", placeholder = "Search WHO ICD-10 by disease or code…" }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [rows, busy] = useSearch(q);
  const box = useRef(null);
  useEffect(() => {
    const off = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", off);
    return () => document.removeEventListener("mousedown", off);
  }, []);
  const pick = (d) => {
    const x = { code: d.code, title: d.title, valid: true };
    onChange(multiple ? [...value.filter((v) => v.code !== d.code), x] : [x]);
    setQ(""); setOpen(false);
  };
  return (
    <div ref={box} className="relative" data-testid={testid}>
      {value.length > 0 && <div className="mb-1.5 flex flex-wrap gap-1.5">{value.map((x) => (
        <span key={x.code} className="inline-flex items-center gap-1 rounded border border-sky-200 bg-sky-50 px-2 py-1 text-[12px]" data-testid={`${testid}-chip-${x.code}`}>
          <IcdLabel x={x} />
          <button type="button" data-testid={`${testid}-remove-${x.code}`} onClick={() => onChange(value.filter((v) => v.code !== x.code))} className="text-slate-400 hover:text-red-600"><X size={12} /></button>
        </span>
      ))}</div>}
      {(multiple || value.length === 0) && (
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
          <input data-testid={`${testid}-input`} value={q} onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }} placeholder={placeholder}
            className="w-full rounded border border-slate-200 bg-white py-1.5 pl-8 pr-8 text-sm outline-none focus:border-sky-400" />
          {busy && <Loader2 size={14} className="absolute right-2.5 top-2.5 animate-spin text-slate-400" />}
        </div>
      )}
      {open && q.trim().length >= 2 && (
        <ul className="absolute z-50 mt-1 max-h-72 w-full overflow-y-auto rounded border border-slate-200 bg-white py-1 shadow-lg" data-testid={`${testid}-results`}>
          {rows.map((d) => (
            <li key={d.code}><button type="button" data-testid={`${testid}-option-${d.code}`} onClick={() => pick(d)} className="flex w-full items-baseline gap-2 px-3 py-1.5 text-left text-[13px] hover:bg-sky-50">
              <b className="w-14 shrink-0 font-mono text-[12px]">{d.code}</b><span className="min-w-0 flex-1">{d.title}<span className="block text-[10px] text-slate-400">{d.chapter_title}</span></span>
            </button></li>
          ))}
          {!busy && rows.length === 0 && <li className="px-3 py-2 text-[12px] text-slate-500">No WHO ICD-10 match</li>}
        </ul>
      )}
      <p className="mt-1 text-[10px] text-slate-400">Codes from the WHO International Classification of Diseases (ICD-10, 2019)</p>
    </div>
  );
}
